import {
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { unwrap } from '../api/client';
import { useOrg } from '../api/org';
import {
  createEnvironment,
  deleteEnvironment,
  deleteEnvironmentConfig,
  deleteProjectConfig,
  listEnvironmentConfig,
  listEnvironments,
  listProjectConfig,
  resolvedEnvironmentConfig,
  setEnvironmentConfig,
  setProjectConfig,
  type Environment,
  type EnvironmentCreate,
} from '../api/gen';
import { ConfigEditor } from '../components/ConfigEditor';
import { ProjectSelect, useProjectParam } from '../components/ProjectSelect';
import { Refusal } from '../components/Refusal';

type Kind = EnvironmentCreate['kind'];

const kinds: { value: Kind; label: string }[] = [
  { value: 'dev', label: 'dev — one per project' },
  { value: 'prod', label: 'prod — one per project' },
  { value: 'staging', label: 'staging' },
  { value: 'preview', label: 'preview' },
];

const colours: Record<Kind, string> = { dev: 'blue', staging: 'yellow', prod: 'red', preview: 'grape' };

/**
 * A project's environments and the configuration each one sees.
 *
 * Configuration is two layers: the project's base, which every environment
 * inherits, and each environment's own overrides. A run sees the second laid
 * over the first, which is what "Resolved" shows — with secrets reduced to
 * their keys, because the server never sends a secret's value back.
 */
export function EnvironmentsPage() {
  const queries = useQueryClient();
  const headers = useOrg();
  const [project, setProject] = useProjectParam();
  const [params, setParams] = useSearchParams();
  const selected = params.get('env') ?? '';
  const select = (id: string) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('env', id);
      else next.delete('env');
      return next;
    });

  const [opened, { open, close }] = useDisclosure();
  const [kind, setKind] = useState<Kind>('staging');
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');

  const envs = useQuery({
    queryKey: ['environments', project],
    queryFn: () => unwrap(listEnvironments({ headers, path: { project } })),
    enabled: !!project,
  });

  const create = useMutation({
    mutationFn: () => {
      const body: EnvironmentCreate = { kind };
      if (slug) body.slug = slug;
      if (name) body.name = name;
      return unwrap(createEnvironment({ headers, path: { project }, body }));
    },
    onSuccess: (env) => {
      queries.invalidateQueries({ queryKey: ['environments', project] });
      setSlug('');
      setName('');
      close();
      select(env.id);
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => unwrap(deleteEnvironment({ headers, path: { id } })),
    onSuccess: (_, id) => {
      queries.invalidateQueries({ queryKey: ['environments', project] });
      if (id === selected) select('');
    },
  });

  const env = envs.data?.environments.find((e) => e.id === selected);
  const needsSlug = kind === 'staging' || kind === 'preview';

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Environments</Title>
        <Group>
          <ProjectSelect value={project} onChange={setProject} />
          <Button onClick={open} disabled={!project}>
            New environment
          </Button>
        </Group>
      </Group>

      <Text c="dimmed" size="sm">
        Where an agent runs, and with which values. <code>mindvm dev</code> makes
        the dev environment on its first start; <code>mindvm deploy --env</code>{' '}
        points one at a published version.
      </Text>

      <Refusal error={envs.error} />
      <Refusal error={del.error} />

      <Card withBorder p={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Environment</Table.Th>
              <Table.Th>Kind</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {envs.isLoading && (
              <Table.Tr>
                <Table.Td colSpan={4}>Loading…</Table.Td>
              </Table.Tr>
            )}
            {envs.data?.environments.map((e) => (
              <Table.Tr
                key={e.id}
                onClick={() => select(e.id)}
                style={{ cursor: 'pointer' }}
                bg={e.id === selected ? 'var(--mantine-primary-color-light)' : undefined}
              >
                <Table.Td>
                  <code>{e.slug}</code>
                  <Text size="xs" c="dimmed">
                    {e.name}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" color={colours[e.kind]}>
                    {e.kind}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{new Date(e.createdAt).toLocaleString()}</Text>
                </Table.Td>
                <Table.Td>
                  <Group justify="flex-end">
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="red"
                      loading={del.isPending && del.variables === e.id}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        if (confirm(`Delete ${e.slug} and its configuration?`)) del.mutate(e.id);
                      }}
                    >
                      Delete
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {envs.data?.environments.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed">No environments in this project yet.</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {!project && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed">Make a project first.</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      {project && (
        <Tabs defaultValue={env ? 'env' : 'base'} key={env?.id ?? 'base'} keepMounted={false}>
          <Tabs.List>
            {env && <Tabs.Tab value="env">{env.slug} overrides</Tabs.Tab>}
            {env && <Tabs.Tab value="resolved">Resolved</Tabs.Tab>}
            <Tabs.Tab value="base">Project base</Tabs.Tab>
          </Tabs.List>
          {env && (
            <Tabs.Panel value="env" pt="md">
              <EnvironmentConfig env={env} />
            </Tabs.Panel>
          )}
          {env && (
            <Tabs.Panel value="resolved" pt="md">
              <Resolved env={env} />
            </Tabs.Panel>
          )}
          <Tabs.Panel value="base" pt="md">
            <ProjectConfig project={project} />
          </Tabs.Panel>
        </Tabs>
      )}

      <Modal opened={opened} onClose={close} title="New environment">
        <Stack>
          <Select
            label="Kind"
            data={kinds}
            value={kind}
            allowDeselect={false}
            onChange={(v) => v && setKind(v as Kind)}
          />
          <TextInput
            label="Slug"
            description={
              needsSlug ? 'Required for staging and preview.' : 'dev and prod are named after their kind.'
            }
            disabled={!needsSlug}
            value={needsSlug ? slug : kind}
            onChange={(e) => setSlug(e.currentTarget.value)}
          />
          <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <Refusal error={create.error} />
          <Button
            disabled={needsSlug && !slug}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}

function EnvironmentConfig({ env }: { env: Environment }) {
  const queries = useQueryClient();
  const headers = useOrg();
  const id = env.id;
  const entries = useQuery({
    queryKey: ['env-config', id],
    queryFn: () => unwrap(listEnvironmentConfig({ headers, path: { id } })),
  });
  const refresh = () => {
    queries.invalidateQueries({ queryKey: ['env-config', id] });
    queries.invalidateQueries({ queryKey: ['env-resolved', id] });
  };
  return (
    <ConfigEditor
      entries={entries.data?.entries}
      loading={entries.isLoading}
      error={entries.error}
      empty="No overrides: this environment sees the project base as it is."
      onSet={(key, body) =>
        unwrap(setEnvironmentConfig({ headers, path: { id, key }, body })).then(refresh)
      }
      onDelete={(key) => unwrap(deleteEnvironmentConfig({ headers, path: { id, key } })).then(refresh)}
    />
  );
}

function ProjectConfig({ project }: { project: string }) {
  const queries = useQueryClient();
  const headers = useOrg();
  const entries = useQuery({
    queryKey: ['project-config', project],
    queryFn: () => unwrap(listProjectConfig({ headers, path: { project } })),
  });
  const refresh = () => {
    queries.invalidateQueries({ queryKey: ['project-config', project] });
    queries.invalidateQueries({ queryKey: ['env-resolved'] });
  };
  return (
    <ConfigEditor
      entries={entries.data?.entries}
      loading={entries.isLoading}
      error={entries.error}
      empty="No project-level values."
      onSet={(key, body) =>
        unwrap(setProjectConfig({ headers, path: { project, key }, body })).then(refresh)
      }
      onDelete={(key) => unwrap(deleteProjectConfig({ headers, path: { project, key } })).then(refresh)}
    />
  );
}

function Resolved({ env }: { env: Environment }) {
  const headers = useOrg();
  const { data, isLoading, error } = useQuery({
    queryKey: ['env-resolved', env.id],
    queryFn: () => unwrap(resolvedEnvironmentConfig({ headers, path: { id: env.id } })),
  });
  return (
    <Stack>
      <Text c="dimmed" size="sm">
        What a run in <code>{env.slug}</code> sees: the project base overlaid by
        this environment&rsquo;s overrides. Secret values are never sent here.
      </Text>
      <Refusal error={error} />
      {data && data.dropped.length > 0 && (
        <Text c="red" size="sm">
          Cannot be decrypted with the configured key, so a run will not see them:{' '}
          {data.dropped.map((k) => (
            <code key={k}>{k} </code>
          ))}
        </Text>
      )}
      <Card withBorder p={0}>
        <Table striped>
          <Table.Tbody>
            {isLoading && (
              <Table.Tr>
                <Table.Td>Loading…</Table.Td>
              </Table.Tr>
            )}
            {data?.values.map((e) => (
              <Table.Tr key={e.key}>
                <Table.Td>
                  <code>{e.key}</code>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" ff="monospace" c={e.isSecret ? 'dimmed' : undefined}>
                    {e.isSecret ? (e.hasValue ? '••••••••' : '(not set)') : e.value}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
            {data?.values.length === 0 && (
              <Table.Tr>
                <Table.Td>
                  <Text c="dimmed">Nothing is set.</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
